// Flaunté Shopify Theme JS
// Load order: alpine-collapse → alpine-intersect → gsap → ScrollTrigger → theme.js → alpine.min.js (LAST)
// This file registers Alpine stores/data via alpine:init event and initialises GSAP on DOMContentLoaded.

(function () {
  'use strict';

  var CART_AUTO_OPENED_KEY = 'flaunte-cart-auto-opened';

  function hasAutoOpenedCartDrawer() {
    try {
      return window.sessionStorage.getItem(CART_AUTO_OPENED_KEY) === 'true';
    } catch (e) {
      return false;
    }
  }

  function markCartDrawerAutoOpened() {
    try {
      window.sessionStorage.setItem(CART_AUTO_OPENED_KEY, 'true');
    } catch (e) {}
  }

  // ─────────────────────────────────────────────────────
  // Alpine Init
  // ─────────────────────────────────────────────────────
  document.addEventListener('alpine:init', function () {

    // ── Cart Store (Shopify AJAX API) ─────────────────
    Alpine.store('cart', {
      open: false,
      items: [],
      loading: false,

      async init() {
        try {
          var data = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
          this.items = data.items;
        } catch (e) {}
      },

      async add(variantId, quantity) {
        this.loading = true;
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: quantity || 1 }),
          });
          await this.refresh();
          if (!hasAutoOpenedCartDrawer()) {
            this.open = true;
            markCartDrawerAutoOpened();
          }
        } catch (e) {}
        this.loading = false;
      },

      async refresh() {
        try {
          var data = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
          this.items = data.items;
        } catch (e) {}
      },

      async update(key, quantity) {
        this.loading = true;
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: key, quantity: quantity }),
          });
          await this.refresh();
        } catch (e) {}
        this.loading = false;
      },

      remove(key) { return this.update(key, 0); },
      inc(key, qty) { return this.update(key, qty + 1); },
      dec(key, qty) { return this.update(key, qty > 1 ? qty - 1 : 0); },

      get count() { return this.items.reduce(function (s, i) { return s + i.quantity; }, 0); },
      get total() { return (this.items.reduce(function (s, i) { return s + i.final_line_price; }, 0) / 100).toFixed(2); },
    });

    // ── Modal Store (size guide quick-view) ───────────
    Alpine.store('modal', {
      open: false,
      show() { this.open = true; },
      hide() { this.open = false; },
    });

    // ── productCard component ─────────────────────────
    // Reads variant ID from the wrapping element's data attributes.
    Alpine.data('productCard', function () {
      return {
        addedToCart: false,

        async quickAdd() {
          var variantId = parseInt(this.$el.dataset.variantId);
          if (!variantId) return;
          await this.$store.cart.add(variantId, 1);
          this.addedToCart = true;
          var self = this;
          setTimeout(function () { self.addedToCart = false; }, 2000);
        },
      };
    });

    // ── collectionsPage component ─────────────────────
    Alpine.data('collectionsPage', function (config) {
      config = config || {};
      return {
        category: config.initialCategory || 'all',
        sort: config.initialSort || config.defaultSort || 'manual',
        defaultSort: config.defaultSort || 'manual',
        collectionUrl: config.collectionUrl || window.location.pathname,
        visibleCount: 0,
        filtersOpen: false,
        hasInteracted: false,

        init() {
          var self = this;
          this.$watch('category', function () {
            self.hasInteracted = true;
            self.applyFilter();
          });
          this.$watch('sort', function () {
            self.hasInteracted = true;
            self.applyNativeSort();
          });
          this.applyFilter();
        },

        applyFilter() {
          var cat = this.category;
          var visibleCount = 0;

          Array.from(this.$el.querySelectorAll('.collection-card')).forEach(function (card) {
            var cardCategory = card.dataset.productCategory || card.dataset.productType;
            var isVisible = cat === 'all' || cardCategory === cat;
            card.style.display = isVisible ? '' : 'none';
            if (isVisible) visibleCount += 1;
          });

          this.visibleCount = visibleCount;

          this.updateCategoryUrl();
          this.syncVisibleCards(Array.from(this.$el.querySelectorAll('.collection-card')));
        },

        applyNativeSort() {
          var url = new URL(window.location.href);
          var sortValue = this.sort || this.defaultSort;

          url.pathname = this.collectionUrl;

          if (!sortValue || sortValue === this.defaultSort) {
            url.searchParams.delete('sort_by');
          } else {
            url.searchParams.set('sort_by', sortValue);
          }

          if (this.category && this.category !== 'all') {
            url.searchParams.set('category', this.category);
          } else {
            url.searchParams.delete('category');
          }

          window.location.href = url.toString();
        },

        updateCategoryUrl() {
          var url = new URL(window.location.href);

          if (this.category && this.category !== 'all') {
            url.searchParams.set('category', this.category);
          } else {
            url.searchParams.delete('category');
          }

          window.history.replaceState({}, '', url.toString());
        },

        syncVisibleCards(cards) {
          var visibleCards = cards.filter(function (card) {
            return card.style.display !== 'none';
          });
          var hiddenCards = cards.filter(function (card) {
            return card.style.display === 'none';
          });

          if (typeof gsap !== 'undefined') {
            gsap.set(hiddenCards, { opacity: 0, y: 50 });

            if (!this.hasInteracted) {
              if (typeof ScrollTrigger !== 'undefined' && ScrollTrigger.refresh) {
                ScrollTrigger.refresh();
              }
              return;
            }

            gsap.killTweensOf(visibleCards);
            gsap.set(visibleCards, { opacity: 0, y: 18 });
            gsap.to(visibleCards, {
              opacity: 1,
              y: 0,
              duration: 0.35,
              ease: 'power2.out',
              stagger: 0.05,
              overwrite: true,
            });
          } else {
            hiddenCards.forEach(function (card) {
              card.style.opacity = '0';
              card.style.transform = 'translateY(50px)';
            });
            visibleCards.forEach(function (card) {
              card.style.opacity = '1';
              card.style.transform = 'translateY(0)';
            });
          }

          if (typeof ScrollTrigger !== 'undefined' && ScrollTrigger.refresh) {
            ScrollTrigger.refresh();
          }
        },

        clearFilters() {
          this.category = 'all';
          this.sort = 'featured';
          this.applyFilter();
        },
      };
    });

    // ── pdp component (product detail page) ──────────
    Alpine.data('pdp', function () {
      return {
        quantity: 1,
        addedToCart: false,
        activeAccordion: 'details',
        sizeGuideOpen: false,
        sizeGuideSlides: [],
        activeSizeGuideIndex: 0,
        activeImageIndex: 0,

        init() {
          this.sizeGuideSlides = (window.Flaute && window.Flaute.sizeGuideSlides) ? window.Flaute.sizeGuideSlides : [];
        },

        setActiveImage(index) { this.activeImageIndex = index; },

        get activeSizeGuideSlide() {
          return this.sizeGuideSlides[this.activeSizeGuideIndex] || {};
        },

        formatPrice(cents) {
          return '$' + (cents / 100).toFixed(2);
        },

        async addToCart() {
          var variantId = parseInt(this.$el.dataset.variantId);
          if (!variantId) return;
          await this.$store.cart.add(variantId, this.quantity);
          this.addedToCart = true;
          var self = this;
          setTimeout(function () { self.addedToCart = false; }, 3000);
        },

        get addToCartLabel() {
          if (this.addedToCart) return 'Added to cart ✓';
          var unitPrice = parseInt(this.$el.dataset.unitPrice) || 0;
          return 'Add to Cart — ' + this.formatPrice(unitPrice * this.quantity);
        },

        inc() { this.quantity += 1; },
        dec() { if (this.quantity > 1) this.quantity -= 1; },

        openSizeGuide(index) { this.activeSizeGuideIndex = index || 0; this.sizeGuideOpen = true; },
        closeSizeGuide() { this.sizeGuideOpen = false; },
        nextSizeGuide() { this.activeSizeGuideIndex = (this.activeSizeGuideIndex + 1) % this.sizeGuideSlides.length; },
        prevSizeGuide() { this.activeSizeGuideIndex = (this.activeSizeGuideIndex - 1 + this.sizeGuideSlides.length) % this.sizeGuideSlides.length; },
        setSizeGuide(index) { this.activeSizeGuideIndex = index; },
        toggleAccordion(key) { this.activeAccordion = this.activeAccordion === key ? null : key; },
      };
    });

  }); // end alpine:init

  // ─────────────────────────────────────────────────────
  // GSAP Animations
  // ─────────────────────────────────────────────────────
  function initGSAP() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
    gsap.registerPlugin(ScrollTrigger);

    ScrollTrigger.batch('.product-card', {
      onEnter: function (els) {
        gsap.fromTo(els, { opacity: 0, y: 65 }, { opacity: 1, y: 0, duration: 0.75, ease: 'power2.out', stagger: 0.08 });
      },
      start: 'top 88%', once: true,
    });

    ScrollTrigger.batch('.bundle-card', {
      onEnter: function (els) {
        gsap.fromTo(els, { opacity: 0, y: 48, scale: 0.97 }, { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: 'power2.out', stagger: 0.12 });
      },
      start: 'top 88%', once: true,
    });

    if (document.querySelector('.bundle-parallax-bg')) {
      gsap.to('.bundle-parallax-bg', {
        yPercent: 18, ease: 'none',
        scrollTrigger: { trigger: '.bundle-parallax-bg', start: 'top bottom', end: 'bottom top', scrub: 1.2 },
      });
    }

    ScrollTrigger.batch('.brand-pillar', {
      onEnter: function (els) {
        gsap.fromTo(els, { opacity: 0, y: 36 }, { opacity: 1, y: 0, duration: 0.75, ease: 'power2.out', stagger: 0.09 });
      },
      start: 'top 88%', once: true,
    });

    ScrollTrigger.batch('.trust-item', {
      onEnter: function (els) {
        gsap.fromTo(els, { opacity: 0 }, { opacity: 1, duration: 0.6, ease: 'power2.out', stagger: 0.07 });
      },
      start: 'top 90%', once: true,
    });

    ScrollTrigger.batch('.collection-card', {
      onEnter: function (els) {
        gsap.fromTo(els, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 0.65, ease: 'power2.out', stagger: 0.07 });
      },
      start: 'top 90%', once: true,
    });

    gsap.utils.toArray('.section-title').forEach(function (el) {
      gsap.fromTo(el, { opacity: 0, y: 38 }, {
        opacity: 1, y: 0, duration: 1, ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 86%', once: true },
      });
    });

    var orbConfigs = [
      { sel: '.orb-1', yPct: -24, scrub: 1.0 },
      { sel: '.orb-2', yPct: -40, scrub: 1.5 },
      { sel: '.orb-3', yPct: -18, scrub: 1.2 },
      { sel: '.orb-4', yPct: -32, scrub: 0.8 },
    ];
    orbConfigs.forEach(function (cfg) {
      if (document.querySelector(cfg.sel)) {
        gsap.to(cfg.sel, {
          yPercent: cfg.yPct, ease: 'none',
          scrollTrigger: { trigger: 'body', start: 'top top', end: 'bottom bottom', scrub: cfg.scrub },
        });
      }
    });
  }

  // ─────────────────────────────────────────────────────
  // Hero Scroll Video (no-op if no video element)
  // ─────────────────────────────────────────────────────
  function initHeroScrollVideo() {
    var video = document.getElementById('hero-video');
    var container = document.getElementById('hero-scroll-container');
    if (!video || !container) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');
    video.pause();
    video.currentTime = 0;

    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

    var mm = gsap.matchMedia();
    var blobURL = null;
    var src = video.currentSrc || video.src;

    function onVideoReady(cb) {
      if (video.readyState >= 2) { cb(); return; }
      var done = false;
      var fn = function () { if (done) return; done = true; video.removeEventListener('loadeddata', fn); video.removeEventListener('canplay', fn); cb(); };
      video.addEventListener('loadeddata', fn);
      video.addEventListener('canplay', fn);
    }

    mm.add('(prefers-reduced-motion: no-preference)', function () {
      var tl = null;

      function buildScrub() {
        if (tl) { tl.scrollTrigger && tl.scrollTrigger.kill(); tl.kill(); }
        var maxTime = Math.max((video.duration || 1) - 0.05, 0.01);
        var ph = { currentTime: 0 };
        tl = gsap.timeline({
          scrollTrigger: { trigger: container, start: 'top top', end: 'bottom bottom', scrub: true, invalidateOnRefresh: true },
        });
        tl.fromTo(ph, { currentTime: 0 }, {
          currentTime: maxTime, ease: 'none',
          onUpdate: function () {
            var t = Math.min(ph.currentTime, maxTime);
            if (Math.abs(video.currentTime - t) < 1 / 30) return;
            try { video.currentTime = t; } catch (e) {}
          },
        });
        ScrollTrigger.refresh();
      }

      onVideoReady(buildScrub);

      return function () {
        if (tl) { tl.scrollTrigger && tl.scrollTrigger.kill(); tl.kill(); }
        if (blobURL) { URL.revokeObjectURL(blobURL); blobURL = null; }
      };
    });

    mm.add('(prefers-reduced-motion: reduce)', function () {
      gsap.set(video, { opacity: 1 });
    });
  }

  function initPdpControls() {
    Array.from(document.querySelectorAll('[data-pdp-root]')).forEach(function (root) {
      if (root.dataset.pdpBound === 'true') return;

      var quantityEl = root.querySelector('[data-pdp-quantity]');
      var decrementButton = root.querySelector('[data-pdp-decrement]');
      var incrementButton = root.querySelector('[data-pdp-increment]');
      var addButton = root.querySelector('[data-pdp-add]');
      var addLabel = root.querySelector('[data-pdp-add-label]');
      var addSpinner = root.querySelector('[data-pdp-add-spinner]');
      var quantityInput = root.querySelector('[data-pdp-quantity-input]');

      if (!quantityEl || !addButton || !addLabel) return;

      root.dataset.pdpBound = 'true';

      var quantity = 1;
      var addedToCart = false;
      var isLoading = false;
      var resetLabelTimer = null;

      function formatPrice(cents) {
        return '$' + (cents / 100).toFixed(2);
      }

      function render() {
        var unitPrice = parseInt(root.dataset.unitPrice, 10) || 0;
        quantityEl.textContent = String(quantity);

        if (quantityInput) {
          quantityInput.value = String(quantity);
        }

        if (isLoading) {
          addLabel.textContent = 'Adding...';
        } else if (addedToCart) {
          addLabel.textContent = 'Added to cart ✓';
        } else {
          addLabel.textContent = 'Add to Cart — ' + formatPrice(unitPrice * quantity);
        }

        if (addSpinner) {
          addSpinner.hidden = !isLoading;
        }

        addButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        addButton.style.opacity = isLoading ? '0.82' : '1';
        addButton.style.cursor = isLoading ? 'wait' : 'pointer';
      }

      if (decrementButton) {
        decrementButton.addEventListener('click', function (event) {
          event.preventDefault();
          if (quantity > 1) {
            quantity -= 1;
            addedToCart = false;
            render();
          }
        });
      }

      if (incrementButton) {
        incrementButton.addEventListener('click', function (event) {
          event.preventDefault();
          quantity += 1;
          addedToCart = false;
          render();
        });
      }

      addButton.addEventListener('click', async function (event) {
        event.preventDefault();

        var variantId = parseInt(root.dataset.variantId, 10);
        if (!variantId || addButton.disabled) return;

        addButton.disabled = true;
        isLoading = true;
        render();

        try {
          var cartStore = window.Alpine && typeof window.Alpine.store === 'function' ? window.Alpine.store('cart') : null;

          if (cartStore && typeof cartStore.add === 'function') {
            await cartStore.add(variantId, quantity);
          } else {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: quantity }),
            });
          }

          addedToCart = true;

          clearTimeout(resetLabelTimer);
          resetLabelTimer = setTimeout(function () {
            addedToCart = false;
            render();
          }, 3000);
        } catch (e) {
          addedToCart = false;
        } finally {
          isLoading = false;
          addButton.disabled = false;
          render();
        }
      });

      render();
    });
  }

  function initQuickAddButtons() {
    Array.from(document.querySelectorAll('[data-quick-add-root]')).forEach(function (root) {
      if (root.dataset.quickAddBound === 'true') return;

      var quickAddButton = root.querySelector('[data-quick-add-button]');
      var quickAddLabel = root.querySelector('[data-quick-add-label]');
      var quickAddSpinner = root.querySelector('[data-quick-add-spinner]');

      if (!quickAddButton || !quickAddLabel) return;

      root.dataset.quickAddBound = 'true';

      var isLoading = false;
      var resetLabelTimer = null;

      function renderQuickAddLabel() {
        if (isLoading) {
          quickAddLabel.textContent = 'Adding...';
        } else {
          quickAddLabel.textContent = 'Quick Add';
        }

        if (quickAddSpinner) {
          quickAddSpinner.hidden = !isLoading;
        }

        quickAddButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        quickAddButton.style.opacity = isLoading ? '0.82' : '1';
        quickAddButton.style.cursor = isLoading ? 'wait' : 'pointer';
      }

      quickAddButton.addEventListener('click', async function (event) {
        event.preventDefault();
        event.stopPropagation();

        var variantId = parseInt(root.dataset.variantId, 10);
        if (!variantId || quickAddButton.disabled) return;

        quickAddButton.disabled = true;
        isLoading = true;
        renderQuickAddLabel();

        try {
          var cartStore = window.Alpine && typeof window.Alpine.store === 'function' ? window.Alpine.store('cart') : null;

          if (cartStore && typeof cartStore.add === 'function') {
            await cartStore.add(variantId, 1);
          } else {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1 }),
            });
          }

          isLoading = false;
          if (quickAddSpinner) {
            quickAddSpinner.hidden = true;
          }
          quickAddLabel.textContent = 'Added ✓';
          clearTimeout(resetLabelTimer);
          resetLabelTimer = setTimeout(function () {
            renderQuickAddLabel();
          }, 2000);
        } catch (e) {
          isLoading = false;
          renderQuickAddLabel();
        } finally {
          quickAddButton.disabled = false;
          if (quickAddLabel.textContent !== 'Added ✓') {
            renderQuickAddLabel();
          }
        }
      });

      renderQuickAddLabel();
    });
  }

  function scrollToCurrentHash() {
    if (!window.location.hash) return;
    var target = document.querySelector(window.location.hash);
    if (!target) return;
    var offset = window.innerWidth >= 768 ? 120 : 104;
    var top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  function scheduleHashScroll() {
    if (!window.location.hash) return;

    requestAnimationFrame(function () {
      requestAnimationFrame(scrollToCurrentHash);
    });

    window.setTimeout(scrollToCurrentHash, 180);
    window.setTimeout(scrollToCurrentHash, 420);
  }

  // ─────────────────────────────────────────────────────
  // Boot
  // ─────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    initGSAP();
    initHeroScrollVideo();
    initPdpControls();
    initQuickAddButtons();
    scheduleHashScroll();
  });

  window.addEventListener('load', scheduleHashScroll, { once: true });
  window.addEventListener('hashchange', scrollToCurrentHash);

}());
